/**
 * Funnel Section
 *
 * Visual conversion funnel with drop-off percentages.
 * Uses horizontal bar chart for funnel visualization.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import type { FunnelSummary } from '../../../types/analytics';
import { FunnelSkeleton } from './AnalyticsSkeleton';
import { AlertsBanner } from './AlertsBanner';

// Colors for funnel steps (gradient from purple to blue)
const FUNNEL_COLORS = [
  '#a855f7', '#9333ea', '#7c3aed', '#6366f1', '#4f46e5', '#3b82f6', '#2563eb',
];

interface FunnelSectionProps {
  funnel: FunnelSummary | null;
  loading: boolean;
}

export const FunnelSection: React.FC<FunnelSectionProps> = ({ funnel, loading }) => {
  if (loading || !funnel) {
    return <FunnelSkeleton />;
  }

  const maxCount = Math.max(...funnel.steps.map((s) => s.count), 1);

  return (
    <div className="space-y-4">
      {/* Alerts */}
      {funnel.alerts.length > 0 && <AlertsBanner alerts={funnel.alerts} />}

      {/* Summary Card */}
      <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Conversion Funnel</h3>
          <span className="text-xs text-gray-400">{funnel.period_days}d period</span>
        </div>

        <div className="flex gap-4 mb-4">
          <div className="text-center">
            <p className="text-2xl font-extrabold text-gray-900">
              {funnel.overall_rate}%
            </p>
            <p className="text-[10px] text-gray-400 uppercase">Overall Rate</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-extrabold text-gray-900">
              {funnel.total_entries.toLocaleString()}
            </p>
            <p className="text-[10px] text-gray-400 uppercase">Entries</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-extrabold text-gray-900">
              {funnel.total_conversions.toLocaleString()}
            </p>
            <p className="text-[10px] text-gray-400 uppercase">Conversions</p>
          </div>
        </div>

        {/* Funnel Steps */}
        <div className="space-y-2">
          {funnel.steps.map((step, idx) => {
            const widthPct = maxCount > 0 ? (step.count / maxCount) * 100 : 0;

            return (
              <motion.div
                key={step.step_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="font-medium text-gray-600">{step.step_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{step.count.toLocaleString()}</span>
                    {idx > 0 && (
                      <span className={`text-[10px] ${step.drop_off_rate > 50 ? 'text-red-500' : 'text-gray-400'}`}>
                        -{step.drop_off_rate}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-6 bg-gray-100 rounded-lg overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${widthPct}%` }}
                    transition={{ duration: 0.6, delay: idx * 0.08 }}
                    className="h-full rounded-lg"
                    style={{ backgroundColor: FUNNEL_COLORS[idx % FUNNEL_COLORS.length] }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Bar Chart View */}
      <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Funnel Chart</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={funnel.steps} layout="vertical">
            <XAxis type="number" tick={{ fontSize: 10 }} />
            <YAxis
              dataKey="step_name"
              type="category"
              tick={{ fontSize: 9 }}
              width={90}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              formatter={(value: unknown) => [Number(value ?? 0).toLocaleString(), 'Users']}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {funnel.steps.map((_, idx) => (
                <Cell key={idx} fill={FUNNEL_COLORS[idx % FUNNEL_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
